import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DeleteDialogComponent } from '../dialogs/delete/delete-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { ServiceDeliveryPointService } from '../shared/services/service-delivery-points.service';
import { LocalesService } from '../shared/services/locales.service';
import { TimezonesService } from '../shared/services/timezones.service';
import { Timezone } from '../shared/model/timezone.model';
import { Locale } from '../shared/model/locale.model';
import {
  statuses as ServiceDeliveryPointStatus,
  ServiceDeliveryPoint,
} from '../shared/model/service-delivery-point.model';
import { CookieService } from '../shared/services/cookie.service';
import { GlobalAlertService } from '../shared/services/global-alert.service';
import { OrganizationsService } from '../shared/services/organizations.service';
import { ServiceAccountCard } from '../shared/model/service-account.model';
import { ContextSelectorService } from 'ngx-global-nav';
import { SiteService } from '../shared/services/sites.service';
import { lastValueFrom } from 'rxjs';
import { ReferenceService, UtilityAccountsService, ExternalPointsService } from '@services';
import { ExternalPoint, Channel } from '@model';
import { SupportingDataService } from '../shared/services/supporting-data.service';
import { Provider } from '../shared/model/external-point.model';

@Component({
  selector: 'app-view',
  templateUrl: './view.component.html',
  styleUrls: ['./view.component.scss', '../shared/shared.styles.scss'],
})
export class ViewComponent implements OnInit, OnDestroy {
  loadingExternalPoints$ = this.externalPointsService.loadingExternalPoints$.asObservable();
  loadingServiceAccounts$ = this.serviceDeliveryPointService.loadingServiceAccounts$.asObservable();
  form: FormGroup;
  serviceDeliveryPointId = '';
  subscriptions = [];
  locales: Locale[];
  timezones: Timezone[];
  statuses = ServiceDeliveryPointStatus;
  serviceDeliveryPointsList: ServiceDeliveryPoint[] = [];
  loadingServiceDeliveryPoint = false;
  serviceDeliveryPoint: ServiceDeliveryPoint;
  serviceAccountList: ServiceAccountCard[] = [];
  externalPoints: ExternalPoint[] = [];
  channels: Channel[] = [];
  dataProviders: Provider[] = [];
  orgId: string;

  CONFLICT = 'There is a data conflict with this ServiceDeliveryPoint';
  readonly mode = 'view';
  reportingIntervals: any;

  constructor(
    private localesService: LocalesService,
    private timezonesService: TimezonesService,
    private cookieService: CookieService,
    private serviceDeliveryPointService: ServiceDeliveryPointService,
    private router: Router,
    private messageService: GlobalAlertService,
    private translateService: TranslateService,
    private organizationsService: OrganizationsService,
    private route: ActivatedRoute,
    public dialog: MatDialog,
    public orgSelectorService: ContextSelectorService,
    public siteService: SiteService,
    private utilityAccountsService: UtilityAccountsService,
    private utilityService: ReferenceService,
    private externalPointsService: ExternalPointsService,
    private supportingDataService: SupportingDataService,
  ) {
    this.serviceDeliveryPoint = {} as any;

    const updatedSuccessfullySub = this.translateService
      .get('service.delivery.account.notification.updated_successfully')
      .subscribe(() => {
        this.CONFLICT = this.translateService.instant('service.delivery.account.notification.conflict');
      });
    this.timezones = timezonesService.timezones;
    const localesSub = this.localesService.locales$.subscribe((locales) => {
      this.locales = locales;
    });
    this.loadDynamicLists();

    const serviceDeliveryPointsSub = this.serviceDeliveryPointService.serviceDeliveryPoints$.subscribe((data) => {
      if (data && !data.hasError) {
        this.serviceDeliveryPointsList = data.sdps;
      }
    });
    const conflictErrorSub = this.serviceDeliveryPointService.conflictError.subscribe(() => {
      this.messageService.setError(this.CONFLICT, 7000);
    });

    this.utilityService.setUtilities();
    this.utilityService.setChannels();
    this.supportingDataService.setDataProviders();
    this.supportingDataService.setReportingIntervals();
    this.subscriptions.push(...[serviceDeliveryPointsSub, conflictErrorSub, updatedSuccessfullySub, localesSub]);
  }

  ngOnInit() {
    this.form = new FormGroup({});
    const routeParamsSub = this.route.params.subscribe(async (params) => {
      if (params.id) {
        this.serviceDeliveryPointId = params.id;
        this.loadingServiceDeliveryPoint = true;
        this.serviceDeliveryPointService.selectedServiceDeliveryPointId$.next(this.serviceDeliveryPointId);
        await this.serviceDeliveryPointService.getServiceDeliveryPoint(params.id);
        await this.serviceDeliveryPointService.getSDPServiceAccounts(params.id);
        if (this.organizationsService.isInit) {
          this.organizationsService.getOrgs();
        }

        const org = this.orgSelectorService.currentContext$.value[0];
        if (org && org.id) {
          this.orgId = org.id;
          const siteId = await this.serviceDeliveryPointService.getParentSiteId(this.orgId, params.id);
          if (siteId) await this.externalPointsService.getExternalPoints(siteId, 'SITE', params.id);
        }

        this.loadingServiceDeliveryPoint = false;
      }
    });

    const serviceDeliveryPointSub = this.serviceDeliveryPointService.serviceDeliveryPoint$.subscribe(
      async (serviceDeliveryPoint) => {
        if (serviceDeliveryPoint) {
          this.serviceDeliveryPoint = serviceDeliveryPoint;
          this.serviceDeliveryPointId = serviceDeliveryPoint.id;
          this.externalPoints = [];
          this.serviceAccountList = [];
        }
      },
    );

    const orgSelectorSub = this.orgSelectorService.currentContext$.subscribe(async (orgs) => {
      const org = orgs[0];
      const firstTime = this.siteService.getIsFirst();
      const selectedSite = this.siteService.getSelectedSite().getValue();
      if (this.serviceDeliveryPointId && org?.id && firstTime && !selectedSite) {
        const siteId = await this.serviceDeliveryPointService.getParentSiteId(org.id, this.serviceDeliveryPointId);
        if (siteId) {
          this.orgId = org.id;
          this.siteService.setSelectedSite(siteId);
          this.siteService.selectSite(siteId);
          this.serviceDeliveryPointService.setSdpParentId(siteId);
          this.serviceDeliveryPointService.selectSDP(this.serviceDeliveryPointId);
          this.externalPointsService.getExternalPoints(siteId, 'SITE', this.serviceDeliveryPointId);
        } else {
          this.externalPointsService.loadingExternalPoints$.next(false);
        }
      }
    });

    const serviceAccountsSub = this.serviceDeliveryPointService.serviceAccounts$.subscribe(async (serviceAccs) => {
      if (serviceAccs) {
        this.serviceAccountList = this.utilityAccountsService.mapServiceAccountsToCards(serviceAccs);
        if (this.serviceAccountList.length > 0) {
          this.serviceAccountList.sort((a, b) => {
            return new Date(b.startDate || 0).getTime() - new Date(a.startDate || 0).getTime();
          });
        }
      }
    });

    const channelsSub = this.utilityService.channels$.subscribe((channels) => {
      if (channels) {
        this.channels = channels;
      }
    });

    const dataProvidersSub = this.supportingDataService.dataProviders$.subscribe((dataProviders) => {
      if (dataProviders) {
        this.dataProviders = dataProviders;
      }
    });

    const reportingIntervalsSub = this.supportingDataService.reportingIntervals$.subscribe((reportingIntervals) => {
      reportingIntervals.sort((a: any, b: any) => {
        if (parseInt(a.id, 10) < parseInt(b.id, 10)) {
          return -1;
        } else if (parseInt(a.id, 10) > parseInt(b.id, 10)) {
          return 1;
        } else {
          return 0;
        }
      });
      this.reportingIntervals = reportingIntervals;
    });

    const externalPointsSub = this.externalPointsService.externalPoints$.subscribe((externalPoints) => {
      if (externalPoints) {
        this.externalPoints = externalPoints;
      }
    });
    this.subscriptions.push(
      ...[
        serviceAccountsSub,
        channelsSub,
        dataProvidersSub,
        reportingIntervalsSub,
        externalPointsSub,
        routeParamsSub,
        serviceDeliveryPointSub,
        orgSelectorSub,
      ],
    );
  }

  get locale() {
    return this.cookieService.getLocale();
  }

  getStatusDisplayLabel(): string {
    if (this.serviceDeliveryPoint && this.serviceDeliveryPoint.status) {
      return this.statuses.find((status) => status.id === this.serviceDeliveryPoint.status).displayLabel;
    }
    return '-';
  }

  getLocaleDisplayLabel(): string {
    if (
      this.locales &&
      this.locales.length > 0 &&
      this.serviceDeliveryPoint &&
      this.serviceDeliveryPoint.defaultLocale
    ) {
      return this.locales.find((locale) => locale.localeName === this.serviceDeliveryPoint.defaultLocale).displayLabel;
    }
    return '-';
  }

  getTimezoneDisplayLabel(): string {
    if (this.timezones.length > 0 && this.serviceDeliveryPoint && this.serviceDeliveryPoint.timezone) {
      return this.timezones.find((timezone) => timezone.timezoneName === this.serviceDeliveryPoint.timezone)
        .displayLabel;
    }
    return '-';
  }

  // TODO: it seems that this function is no longer used
  getSDPData(id, field) {
    if (this.serviceDeliveryPointsList && this.serviceDeliveryPointsList.length > 0) {
      const reg = this.serviceDeliveryPointsList.find((registration) => {
        return registration.id === id;
      });
      if (reg && reg.hasOwnProperty(field)) {
        return reg[field];
      }
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
  }

  handleCancel() {
    this.router.navigate(['list']);
  }

  openDeleteDialog(): void {
    this.dialog.open(DeleteDialogComponent, {
      width: '400px',
      data: {
        serviceDeliveryPointId: this.serviceDeliveryPointId,
      },
    });
  }

  handleEdit() {
    this.externalPointsService.loadingExternalPoints$.next(true);
    this.externalPointsService.externalPoints$.next(null);
    this.router.navigate([`details/${this.serviceDeliveryPointId}/edit`]);
  }

  private async loadDynamicLists() {
    this.localesService.setLocales();
    this.timezonesService.setTimezones();
  }
}
